import React from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FaUsers,
  FaMapMarkerAlt,
  FaComments,
  FaChartBar,
  FaHistory,
  FaBuilding,
  FaFlag
} from "react-icons/fa";

const PollingUnitDashboard = () => {
  const { user } = useAuth();

  const container = {
    width: "100%",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#f8fafc",
    padding: "0",
    margin: "0",
  };

  const contentWrapper = {
    width: "100%",
    maxWidth: "100%",
    padding: "15px",
    boxSizing: "border-box",
  };

  const header = {
    marginBottom: "25px",
    padding: "0 5px",
  };

  const title = {
    fontSize: "22px",
    fontWeight: "700",
    color: "#2563eb",
    marginBottom: "8px",
    lineHeight: "1.3",
  };

  const subtitle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0",
  };

  const statGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
    width: "100%",
  };

  const statCard = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "20px",
    border: "1px solid #e5e7eb",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const statTitle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#2563eb",
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "10px",
  };

  const statValue = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "6px",
  };

  const statTrend = {
    fontSize: "12px",
    color: "#22c55e",
    fontWeight: "500",
  };

  const mainGrid = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
  };

  const card = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    boxSizing: "border-box",
  };

  const sectionTitle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1e293b",
    marginBottom: "18px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f1f5f9",
  };

  const sectionIcon = {
    color: "#2563eb",
    fontSize: "16px",
  };

  const performanceItem = {
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: "8px",
  };

  const performanceDetails = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const performanceName = {
    fontSize: "15px",
    fontWeight: "600",
    color: "#111827",
  };

  const performanceMembers = {
    fontSize: "13px",
    color: "#3b82f6",
    textAlign: "right",
  };

  const performanceChange = {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "8px",
    borderTop: "1px dashed #e5e7eb",
  };

  const performanceChangeLabel = {
    fontSize: "12px",
    color: "#6b7280",
  };

  const activityItem = {
    display: "flex",
    gap: "12px",
    borderBottom: "1px solid #f8fafc",
    padding: "16px 0",
    alignItems: "flex-start",
  };

  const activityIcon = {
    width: "8px",
    height: "8px",
    backgroundColor: "#2563eb",
    borderRadius: "50%",
    marginTop: "8px",
    flexShrink: 0,
  };

  const activityDetails = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const activityTitle = {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
    lineHeight: "1.4",
  };

  const activitySub = {
    fontSize: "13px",
    color: "#3b82f6",
    fontWeight: "500",
  };

  const activityTime = {
    fontSize: "12px",
    color: "#9ca3af",
    alignSelf: "flex-start",
    backgroundColor: "#f8fafc",
    padding: "4px 8px",
    borderRadius: "6px",
    marginTop: "4px",
  };

  // Responsive styles
  const responsiveStyles = `
    @media (min-width: 768px) {
      .dashboard-content-wrapper {
        padding: 25px;
        max-width: 1200px;
        margin: 0 auto;
      }
      
      .dashboard-stat-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin-bottom: 35px;
      }
      
      .dashboard-stat-card {
        padding: 25px;
        minHeight: 160px;
      }
      
      .dashboard-stat-value {
        font-size: 28px;
      }
      
      .dashboard-main-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 25px;
      }
      
      .dashboard-card {
        padding: 25px;
      }
      
      .dashboard-performance-item {
        flex-direction: row;
        align-items: center;
        gap: 0;
      }
      
      .dashboard-performance-details {
        width: auto;
        flex: 1;
      }
      
      .dashboard-performance-members {
        text-align: left;
        margin-top: 4px;
      }
      
      .dashboard-performance-change {
        width: auto;
        border-top: none;
        padding-top: 0;
        justify-content: flex-end;
        flex-direction: column;
        align-items: flex-end;
        min-width: 100px;
      }
      
      .dashboard-activity-details {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }
      
      .dashboard-activity-time {
        align-self: center;
        margin-top: 0;
      }
    }
    
    @media (min-width: 1024px) {
      .dashboard-content-wrapper {
        padding: 30px 40px;
      }
      
      .dashboard-title {
        font-size: 24px;
      }
      
      .dashboard-subtitle {
        font-size: 15px;
      }
    }
    
    @media (max-width: 767px) {
      .dashboard-stat-grid {
        grid-templateColumns: 1fr;
        gap: 12px;
      }
    }
    
    @media (max-width: 480px) {
      .dashboard-stat-grid {
        grid-templateColumns: 1fr;
      }
      
      .dashboard-stat-card {
        padding: 15px;
        min-height: 130px;
      }
    }
  `;

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={container} className="dashboard-container">
        <div style={contentWrapper} className="dashboard-content-wrapper">
          {/* Header */}
          <div style={header}>
            <h2 style={title} className="dashboard-title">Polling Unit Administration Dashboard</h2>
            <p style={subtitle} className="dashboard-subtitle">
              Welcome, {user?.full_name || 'Polling Unit Administrator'}! Manage your polling unit operations
            </p>
          </div>

          {/* Stats Section */}
          <div style={statGrid} className="dashboard-stat-grid">
            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Total Members</span>
                <FaUsers />
              </div>
              <div style={statValue} className="dashboard-stat-value">487</div>
              <div style={statTrend}>+2.1% from last month</div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>Active Members</span>
                <FaFlag />
              </div>
              <div style={statValue} className="dashboard-stat-value">465</div>
              <div style={statTrend}>98% active rate</div>
            </div>

            <div style={statCard} className="dashboard-stat-card">
              <div style={statTitle}>
                <span>New Registrations</span>
                <FaMapMarkerAlt />
              </div>
              <div style={statValue} className="dashboard-stat-value">32</div>
              <div style={statTrend}>+8 from last month</div>
            </div>
          </div>

          {/* Main Grid */}
          <div style={mainGrid} className="dashboard-main-grid">
            {/* Member Categories */}
            <div style={card} className="dashboard-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaChartBar style={sectionIcon} />
                Member Categories
              </h3>

              {[
                { name: "Registered Voters", members: "412", change: "+3.5%" },
                { name: "Party Members", members: "285", change: "+5.2%" },
                { name: "Volunteers", members: "156", change: "+7.8%" },
                { name: "Community Leaders", members: "42", change: "+2.1%" },
                { name: "Youth Members", members: "189", change: "+6.3%" },
              ].map((unit, index) => (
                <div key={index} style={performanceItem} className="dashboard-performance-item">
                  <div style={performanceDetails} className="dashboard-performance-details">
                    <div style={performanceName}>{unit.name}</div>
                    <div style={{...performanceMembers, textAlign: "right"}} className="dashboard-performance-members">
                      {unit.members}
                    </div>
                  </div>
                  <div style={performanceChange} className="dashboard-performance-change">
                    <div style={{color: "#22c55e", fontWeight: "600", fontSize: "14px"}}>
                      {unit.change}
                    </div>
                    <div style={performanceChangeLabel}>growth rate</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Activity Feed */}
            <div style={card} className="dashboard-card dashboard-activity-card">
              <h3 style={sectionTitle} className="dashboard-section-title">
                <FaHistory style={sectionIcon} />
                Recent Activity
              </h3>

              {[
                { title: "New member registration", sub: "John Doe registered", time: "8 minutes ago" },
                { title: "Polling unit broadcast", sub: "Community Announcement", time: "30 minutes ago" },
                { title: "Member status updated", sub: "5 members approved", time: "1 hour ago" },
                { title: "Group chat created", sub: "Polling Unit Coordination", time: "3 hours ago" },
                { title: "Weekly report generated", sub: "Polling Unit Analytics", time: "5 hours ago" },
              ].map((activity, index) => (
                <div key={index} style={activityItem} className="dashboard-activity-item">
                  <div style={activityIcon}></div>
                  <div style={activityDetails} className="dashboard-activity-details">
                    <div style={activityTitle}>{activity.title}</div>
                    <div style={activitySub}>{activity.sub}</div>
                    <div style={activityTime} className="dashboard-activity-time">{activity.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PollingUnitDashboard;